import { DEFAULT_THEME_RADIUS } from "../../canvas/radiusScale.ts";
import type { ColorTokens } from "../../canvas/theme.ts";
import {
  type BuiltinThemeName,
  type FontRoles,
  type SpacingTokens,
  type Theme,
  builtinThemeId,
} from "../theme.ts";

/**
 * The families every built-in wears, and the stacks generated screens have
 * always painted.
 *
 * Written down once here rather than per theme: the ten built-ins differ only
 * in colour, and a pairing chosen per theme is what step 9 adds. The names are
 * separate from the fallbacks because the document's Google Fonts link is built
 * from the names — see {@link ThemeFontFamily}.
 */
export const DEFAULT_FONT_FAMILIES: FontRoles = {
  default: "sans",
  sans: {
    name: "Inter",
    fallback:
      'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
  },
  serif: {
    name: "Source Serif 4",
    fallback:
      '"Inter", ui-serif, Georgia, Cambria, "Times New Roman", Times, serif',
  },
  mono: {
    name: "Geist Mono",
    fallback:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
};

/**
 * Tailwind's own `--spacing`, stated rather than assumed.
 *
 * Emitting the value Tailwind already defaults to moves nothing — which is the
 * point: it makes the plumbing live at zero visual cost, so step 10 moves the
 * base with an editor in front of it instead of wiring the base up and moving
 * every screen in the same release.
 */
export const DEFAULT_SPACING: SpacingTokens = { base: "0.25rem" };

/**
 * A built-in, from the one thing built-ins actually differ in.
 *
 * The display name is the slug capitalised — all ten are one word, and a
 * hand-written `"Blue"` next to `name: "blue"` is a second copy of the same
 * fact. An org's own themes name themselves.
 */
export const defineBuiltinTheme = ({
  name,
  color,
}: {
  name: BuiltinThemeName;
  color: { light: ColorTokens; dark: ColorTokens };
}): Theme => ({
  id: builtinThemeId(name),
  name: name.charAt(0).toUpperCase() + name.slice(1),
  color,
  radius: { base: DEFAULT_THEME_RADIUS },
  spacing: DEFAULT_SPACING,
  typography: { families: DEFAULT_FONT_FAMILIES },
});
